// src/schemas/theme/color-tokens.ts
import { z } from "zod";
var ColorTokenSchema = z.string().regex(
  /^\d{1,3}(\.\d+)?\s+\d{1,3}(\.\d+)?%\s+\d{1,3}(\.\d+)?%$/,
  'Must be a valid HSL color in format "hue saturation% lightness%" (e.g., "240 10% 3.9%")'
).describe("HSL color value in Tailwind format");
var ColorThemeModeSchema = z.object({
  // Core semantic colors
  background: ColorTokenSchema.describe("Primary background color"),
  foreground: ColorTokenSchema.describe("Primary text color"),
  primary: ColorTokenSchema.describe("Primary brand color"),
  primaryForeground: ColorTokenSchema.describe("Text color for primary elements"),
  secondary: ColorTokenSchema.describe("Secondary color"),
  secondaryForeground: ColorTokenSchema.describe("Text color for secondary elements"),
  // Component-specific colors
  card: ColorTokenSchema.describe("Card background color"),
  cardForeground: ColorTokenSchema.describe("Card text color"),
  popover: ColorTokenSchema.describe("Popover background color"),
  popoverForeground: ColorTokenSchema.describe("Popover text color"),
  // State and utility colors
  muted: ColorTokenSchema.describe("Muted background color"),
  mutedForeground: ColorTokenSchema.describe("Muted text color"),
  accent: ColorTokenSchema.describe("Accent background color"),
  accentForeground: ColorTokenSchema.describe("Accent text color"),
  // Form and interactive elements
  input: ColorTokenSchema.describe("Input field background color"),
  border: ColorTokenSchema.describe("Border color"),
  ring: ColorTokenSchema.describe("Focus ring color"),
  // Semantic state colors
  destructive: ColorTokenSchema.describe("Destructive/error color"),
  destructiveForeground: ColorTokenSchema.describe("Text color for destructive elements"),
  success: ColorTokenSchema.describe("Success color"),
  successForeground: ColorTokenSchema.describe("Text color for success elements"),
  warning: ColorTokenSchema.describe("Warning color"),
  warningForeground: ColorTokenSchema.describe("Text color for warning elements"),
  // App-specific colors
  notification: ColorTokenSchema.describe("Notification indicator color")
});
var ColorThemeSchema = z.object({
  light: ColorThemeModeSchema.describe("Light theme color palette"),
  dark: ColorThemeModeSchema.describe("Dark theme color palette")
});
var ColorThemePatchSchema = z.object({
  light: ColorThemeModeSchema.partial().optional(),
  dark: ColorThemeModeSchema.partial().optional()
});
var COLOR_THEME_TOKEN_KEYS = Object.keys(ColorThemeModeSchema.shape);

// src/schemas/theme/default-palette.ts
var DEFAULT_COLOR_THEME = {
  light: {
    background: "0 0% 96%",
    foreground: "240 10% 3.9%",
    card: "0 0% 100%",
    cardForeground: "240 10% 3.9%",
    popover: "0 0% 100%",
    popoverForeground: "240 10% 3.9%",
    primary: "240 5.9% 10%",
    primaryForeground: "0 0% 98%",
    secondary: "240 4.8% 92%",
    secondaryForeground: "240 5.9% 10%",
    muted: "240 4.8% 92%",
    mutedForeground: "240 3.8% 46.1%",
    accent: "240 4.8% 92%",
    accentForeground: "240 5.9% 10%",
    destructive: "0 84.2% 60.2%",
    destructiveForeground: "0 0% 98%",
    success: "142 76% 36%",
    successForeground: "0 0% 98%",
    warning: "43 96% 56%",
    warningForeground: "240 5.9% 10%",
    border: "240 5.9% 90%",
    input: "240 5.9% 90%",
    ring: "240 5.9% 10%",
    notification: "0 84.2% 60.2%"
  },
  dark: {
    background: "240 6% 10%",
    foreground: "0 0% 98%",
    card: "240 10% 3.9%",
    cardForeground: "0 0% 98%",
    popover: "240 10% 3.9%",
    popoverForeground: "0 0% 98%",
    primary: "0 0% 98%",
    primaryForeground: "240 5.9% 10%",
    secondary: "240 3.7% 15.9%",
    secondaryForeground: "0 0% 98%",
    muted: "240 3.7% 15.9%",
    mutedForeground: "240 5% 64.9%",
    accent: "240 3.7% 15.9%",
    accentForeground: "0 0% 98%",
    destructive: "0 72% 51%",
    destructiveForeground: "0 0% 98%",
    success: "142 71% 45%",
    successForeground: "0 0% 98%",
    warning: "43 89% 58%",
    warningForeground: "240 10% 3.9%",
    border: "240 3.7% 15.9%",
    input: "240 3.7% 15.9%",
    ring: "240 4.9% 83.9%",
    notification: "0 72% 51%"
  }
};

// src/schemas/theme/resolve-theme.ts
function resolveMode(input, defaults) {
  const out = {};
  for (const key of COLOR_THEME_TOKEN_KEYS) {
    const candidate = input?.[key];
    const parsed = ColorTokenSchema.safeParse(candidate);
    out[key] = parsed.success ? parsed.data : defaults[key];
  }
  return out;
}
function resolveTheme(spec) {
  return {
    light: resolveMode(spec?.light, DEFAULT_COLOR_THEME.light),
    dark: resolveMode(spec?.dark, DEFAULT_COLOR_THEME.dark)
  };
}

// src/schemas/theme/theme-seed.ts
import { z as z2 } from "zod";
var ThemeSeedsSchema = z2.object({
  primary: ColorTokenSchema,
  accent: ColorTokenSchema
}).strict();
export {
  COLOR_THEME_TOKEN_KEYS,
  ColorThemeModeSchema,
  ColorThemePatchSchema,
  ColorThemeSchema,
  ColorTokenSchema,
  DEFAULT_COLOR_THEME,
  ThemeSeedsSchema,
  resolveTheme
};
